'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { deriveNotifications } from '../lib/derive';
import type { NotificationDay } from '../lib/days';
import type { NotificationThread } from '../lib/threading';
import { useNotificationStore } from './notification-store';
import { useNotificationsData } from './use-notifications-data';

/**
 * Everything both surfaces read, derived once.
 *
 * The bell and the centre are the same model seen at two depths: the bell is
 * what is carried plus today either side of the line, the centre is that plus
 * every day behind it and the preferences that shape all of it. Deriving them
 * together is what keeps the promise that the bell never disagrees with the
 * page.
 */
export interface NotificationsModel {
  readonly at: Date;
  /** Where the line sits. Never null: before the broker moves it, it is the last digest. */
  readonly position: Date;
  /** True while the broker has not moved the line themselves. */
  readonly untouched: boolean;
  readonly carried: readonly NotificationThread[];
  /** Newest first, carried lifted out, repeats folded. */
  readonly fresh: readonly NotificationThread[];
  readonly seen: readonly NotificationThread[];
  readonly days: readonly NotificationDay[];
  readonly onFire: ReadonlySet<string>;
  readonly badge: number;
  readonly snoozed: readonly NotificationThread[];
  readonly done: readonly NotificationThread[];
  /**
   * The bound was reached: there are older lines the page does not hold.
   *
   * The server says so, and it is never derived here. The centre renders it
   * rather than ending quietly, which is the loud-bound rule (plan ruling 12):
   * a surface whose set exceeds the page says so.
   */
  readonly truncated: boolean;
  /** Lines in the whole filtered set. Null when nobody asked for the count. */
  readonly total: number | null;
  /** Lines loaded so far, for the sentence that says what is missing. */
  readonly loaded: number;
  /**
   * What each lens holds over the whole set, at line grain.
   *
   * The toolbar drew this by summing the days it had loaded, so a broker past
   * the bound read a count of the page. These are the server's, from the same
   * statement as the page. Zero for every lens until the first page lands.
   */
  readonly lensCounts: Readonly<Record<'all' | 'needs' | 'mine' | 'firm', number>>;
  readonly hasOlder: boolean;
  /** The read's page window is full; see `ListCeilingNotice`. */
  readonly atCeiling: boolean;
  readonly loadingOlder: boolean;
  /** Ask for the next page. The cursor is the contract's and is finally read. */
  readonly loadOlder: () => void;
}

/**
 * THE NEEDLE IS NOT A PARAMETER ANY MORE. The centre used to hand its search
 * term in and this hook filtered the rows it held, which is the second
 * implementation of a rule the server now owns: the term is sent with the read
 * and the page comes back narrowed, so every row in the store is a match and a
 * filter here would be a no-op that hides a drift. The count beside the box is
 * the server's for the same reason.
 */
export const useNotifications = () => {
  const $ = _c(20);
  const data = useNotificationsData();
  const notifications = useNotificationStore(_temp);
  const preferences = useNotificationStore(_temp2);
  const lastOpenedAt = useNotificationStore(_temp3);
  let t0;
  if ($[0] !== lastOpenedAt || $[1] !== notifications || $[2] !== preferences) {
    t0 = deriveNotifications(notifications, preferences, lastOpenedAt);
    $[0] = lastOpenedAt;
    $[1] = notifications;
    $[2] = preferences;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const derived = t0;
  const t1 = data.figures?.badge ?? 0;
  const t2 = data.figures?.lensAll ?? 0;
  const t3 = data.figures?.lensNeeds ?? 0;
  const t4 = data.figures?.lensMine ?? 0;
  const t5 = data.figures?.lensFirm ?? 0;
  let t6;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4 || $[7] !== t5) {
    t6 = {
      all: t2,
      needs: t3,
      mine: t4,
      firm: t5
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== data.atCeiling || $[10] !== data.fetchNextPage || $[11] !== data.hasNextPage || $[12] !== data.isFetchingNextPage || $[13] !== data.loaded || $[14] !== data.total || $[15] !== data.truncated || $[16] !== derived || $[17] !== t1 || $[18] !== t6) {
    t7 = {
      ...derived,
      badge: t1,
      truncated: data.truncated,
      total: data.total,
      loaded: data.loaded,
      lensCounts: t6,
      hasOlder: data.hasNextPage,
      atCeiling: data.atCeiling,
      loadingOlder: data.isFetchingNextPage,
      loadOlder: data.fetchNextPage
    };
    $[9] = data.atCeiling;
    $[10] = data.fetchNextPage;
    $[11] = data.hasNextPage;
    $[12] = data.isFetchingNextPage;
    $[13] = data.loaded;
    $[14] = data.total;
    $[15] = data.truncated;
    $[16] = derived;
    $[17] = t1;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
};
function _temp(s) {
  return s.notifications;
}
function _temp2(s_0) {
  return s_0.preferences;
}
function _temp3(s_1) {
  return s_1.lastOpenedAt;
}